import { useAuth0 } from '@auth0/auth0-react';
import * as React from 'react';
import { BEEHardware, DeviceId, IBee } from '../../../shared/api-clients/_api';

export interface IListBeesContext {
  selectedBees: string[];
  setSelectedBees: (bee: IBee, selected: boolean) => Promise<void>;
  showCheckboxes: boolean;
  selectedHardwareVersion: BEEHardware;
  clearSelectedBees: () => Promise<void>;
}

export const ListBeesContext = React.createContext<IListBeesContext>(undefined);

export const ListBeesProvider = ({ children }) => {
  const [selectedDevices, setSelectedDevices] = React.useState<DeviceId[]>([]);
  const [authenticated, setAuthenticated] = React.useState<boolean>(false);
  const [selectedHardwareVersion, setSelectedHardwareVersion] = React.useState<BEEHardware>();
  const { getIdTokenClaims } = useAuth0();

  React.useEffect(() => {
    async function userRoles() {
      const claims = await getIdTokenClaims();
      const roles = claims?.['https://sero-digital.sero.life/roles'];
      const rolesAllowedToUpdateBees = ['HCM L3 Support'];
      setAuthenticated(roles?.some(role => rolesAllowedToUpdateBees.includes(role)));
    }

    userRoles();
  }, []);

  React.useEffect(() => {
    // If removing final item from array, reset the hardware version, so that all BEEs can be selected
    if (selectedHardwareVersion && selectedDevices.length === 0) setSelectedHardwareVersion(null);
  }, [selectedDevices]);

  const setSelectedBees = async (bee: IBee, selected: boolean) => {
    if (!selectedHardwareVersion) setSelectedHardwareVersion(bee.hardwareVersion as BEEHardware);
    if (selected) {
      setSelectedDevices([...selectedDevices, bee.deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter(deviceId => deviceId !== bee.deviceId));
    }
  };

  const clearSelectedBees = async () => {
    // Resets selected bees
    setSelectedDevices([]);
    setSelectedHardwareVersion(null);
  };

  return (
    <ListBeesContext.Provider
      value={{
        selectedBees: selectedDevices,
        setSelectedBees,
        showCheckboxes: authenticated,
        selectedHardwareVersion,
        clearSelectedBees,
      }}
    >
      {children}
    </ListBeesContext.Provider>
  );
};

export const useListBees = () => React.useContext(ListBeesContext);
